import { useState } from "react";
import SkewLoader from "react-spinners/SkewLoader";
import { BsExclamationTriangleFill, BsTrash } from "react-icons/bs";
import { MdOutlinePayments } from "react-icons/md";
import DeleteModal from "../../../../../components/modals/DeleteModal";
import useCompanyStore from "../../../../../state-management/useCompanyStore";
import { APP_CONFIG } from "../../../../../helpers/appHelpers";
import {
	PERMISSIONS,
	PermissionChecker,
} from "../../../../../helpers/usePermissionManager";
import { useGetCountry } from "../../../../../services/country.service";
import {
	useGetMerchantRequest,
	useGetMerchantRequestIssues,
	useGetCompanyPaymentSetups,
	useGetDraftedMerchantRequestList,
	useDeleteDraftedMerchantRequest,
} from "../../../../../services/payment-setup.service";
import {
	PaymentSetupDto,
	PaymentSetupOrderBy,
	PaymentSetupStatus,
} from "../../../../../models/payment-setup.dto";
import PaymentSetupWizard, { WizardMode } from "./PaymentSetupWizard";
import RequestSummary from "./RequestSummary";
import AddProductsModal from "./AddProductsModal";
import {
	STATUS_DETAILS,
	getDefaultFormValues,
	paymentSetupToFormValues,
	toList,
	toRecord,
} from "./helpers";

type RequestIssue = {
	sectionCode?: string;
	fieldName?: string;
	issue: string;
	createdAt?: string;
};

const humanise = (value?: string) =>
	(value || "")
		.replace(/([a-z])([A-Z])/g, "$1 $2")
		.replace(/_/g, " ")
		.replace(/^\w/, (char) => char.toUpperCase());

const formatDate = (value?: Date | string | null) =>
	value ? new Date(value).toLocaleDateString(undefined, { dateStyle: "medium" }) : "";

const formatDateTime = (value?: Date | string | null) =>
	value
		? new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
		: "";

const byMostRecent = (a: PaymentSetupDto, b: PaymentSetupDto) =>
	new Date(b.modifiedAt || b.createdAt).getTime() -
	new Date(a.modifiedAt || a.createdAt).getTime();

/** A rough sense of how far along a draft is, from the sections it has data for */
const getDraftProgress = (draft: PaymentSetupDto) => {
	const sections = [
		!!(draft.companyName && draft.tier && draft.companyCategories),
		!!(draft.surname && draft.email),
		!!draft.companyDirectors?.length,
		!!draft.documents?.length,
		!!draft.products?.length,
	];
	return { done: sections.filter(Boolean).length, total: sections.length };
};

const PaymentSetup = () => {
	const { currentCompany } = useCompanyStore();
	const companyId = currentCompany?.id as number;

	const [editingMode, setEditingMode] = useState<WizardMode | null>(null);
	const [activeDraftId, setActiveDraftId] = useState<number | null>(null);
	const [openAddProducts, setOpenAddProducts] = useState(false);
	const [draftToDelete, setDraftToDelete] = useState<PaymentSetupDto | null>(null);

	const { mutate: deleteDraft, isLoading: deletingDraft } = useDeleteDraftedMerchantRequest();
	const handleDeleteDraft = () => {
		if (!draftToDelete) return;
		deleteDraft(
			{ id: draftToDelete.id, companyId },
			{
				onSuccess: () => setDraftToDelete(null),
			},
		);
	};

	const { data: countries, isLoading: loadingCountries } = useGetCountry({
		limit: APP_CONFIG.PAGE_LIMIT,
	});
	const countryCode =
		countries?.data?.find((country) => country.id === currentCompany?.countryId)?.code || "";

	const isDraft = (item: PaymentSetupDto) =>
		String(item.status || "").toUpperCase() === PaymentSetupStatus.DRAFT;
	// Safety net in case the backend ignores the companyId filter
	const belongsToCompany = (item: PaymentSetupDto) =>
		item.companyId == null || Number(item.companyId) === companyId;

	// A company has a single active payment setup: its most recent submitted request
	const { data: requests, isLoading: loadingRequests } = useGetCompanyPaymentSetups(companyId, {
		page: 1,
		limit: 10,
		orderBy: PaymentSetupOrderBy.MODIFIED_AT_DESC,
	});
	const latestRequest = toList<PaymentSetupDto>(requests)
		.filter((item) => belongsToCompany(item) && !isDraft(item))
		.sort(byMostRecent)[0];

	// Drafts are listed separately so the user can pick one to finish
	const { data: draftsResponse, isLoading: loadingDrafts } = useGetDraftedMerchantRequestList(
		companyId,
		{ page: 1, limit: 50, orderBy: PaymentSetupOrderBy.MODIFIED_AT_DESC },
	);
	// Everything from the drafts endpoint is a draft; the company list may include some too
	const drafts = [
		...toList<PaymentSetupDto>(draftsResponse),
		...toList<PaymentSetupDto>(requests).filter(isDraft),
	]
		.filter(belongsToCompany)
		.filter((item, index, all) => all.findIndex((other) => other.id === item.id) === index)
		.sort(byMostRecent);

	const { data: requestDetail, isLoading: loadingDetail } = useGetMerchantRequest(
		latestRequest?.id as number,
	);
	const detail = toRecord<PaymentSetupDto>(requestDetail);
	const request: PaymentSetupDto | undefined = detail?.id ? detail : latestRequest;
	const status = request?.status;

	const { data: issuesResponse } = useGetMerchantRequestIssues(
		request?.id as number,
	);
	const issues = toList<RequestIssue>(issuesResponse);

	// Full details of the draft being resumed
	const { data: activeDraftResponse, isLoading: loadingActiveDraft } = useGetMerchantRequest(
		activeDraftId as number,
	);
	// Fall back to the draft's row from the list if the detail call doesn't return it
	const activeDraftDetail = toRecord<PaymentSetupDto>(activeDraftResponse);
	const activeDraft = activeDraftDetail?.id
		? activeDraftDetail
		: drafts.find((draft) => draft.id === activeDraftId);

	const header = (
		<>
			<h2 className="text-2sm font-semibold mb-3">Payment Setup</h2>
			<hr className="mb-7" />
		</>
	);

	const closeEditing = () => {
		setEditingMode(null);
		setActiveDraftId(null);
	};

	// Starting fresh: saving the first draft refetches this page's data, so keep this
	// branch (and the single wizard inside it) stable until the user leaves it
	if (editingMode === "create") {
		return (
			<div>
				{header}
				<PaymentSetupWizard
					key="new"
					companyId={companyId}
					mode="create"
					initialValues={getDefaultFormValues(countryCode, currentCompany?.name)}
					onCancel={closeEditing}
					onSubmitted={closeEditing}
				/>
			</div>
		);
	}

	if (activeDraftId) {
		if (loadingActiveDraft || loadingCountries || !activeDraft) {
			return (
				<div className="flex justify-center py-20">
					<SkewLoader color="#F58B00" />
				</div>
			);
		}
		return (
			<div>
				{header}
				<PaymentSetupWizard
					key={`draft-${activeDraftId}`}
					companyId={companyId}
					mode="create"
					setupId={activeDraftId}
					initialValues={paymentSetupToFormValues(activeDraft, countryCode)}
					onCancel={() => setActiveDraftId(null)}
					cancelLabel="Back to drafts"
					onSubmitted={closeEditing}
				/>
			</div>
		);
	}

	if (loadingRequests || loadingDrafts || loadingCountries || (latestRequest && loadingDetail)) {
		return (
			<div className="flex justify-center py-20">
				<SkewLoader color="#F58B00" />
			</div>
		);
	}

	const draftsList = drafts.length > 0 && (
		<div className="mt-8 text-left">
			<div className="flex items-center justify-between mb-3">
				<h4 className="font-semibold text-sm">
					Drafts <span className="text-gray-400 font-normal">({drafts.length})</span>
				</h4>
				<p className="text-xs text-gray-500">Pick up where you left off</p>
			</div>
			<ul className="max-h-[420px] overflow-y-auto children-scroll-bar space-y-3 pr-1">
				{drafts.map((draft) => {
					const progress = getDraftProgress(draft);
					return (
						<li
							key={draft.id}
							className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 px-5 py-4 hover:border-primary-blue transition-colors"
						>
							<div className="flex items-center gap-4 min-w-0">
								{draft.companyLogo ? (
									<img
										src={draft.companyLogo}
										alt=""
										className="h-10 w-10 rounded border object-contain shrink-0"
									/>
								) : (
									<div className="h-10 w-10 rounded bg-gray-100 flex items-center justify-center shrink-0">
										<MdOutlinePayments className="text-gray-400" />
									</div>
								)}
								<div className="min-w-0">
									<p className="text-sm font-medium truncate">
										{draft.tradeName || draft.companyName || "Untitled draft"}
									</p>
									<p className="text-xs text-gray-500 mt-0.5">
										Last saved {formatDateTime(draft.modifiedAt || draft.createdAt)}
										{draft.tier && ` · ${draft.tier}`}
									</p>
								</div>
							</div>
							<div className="flex items-center gap-5 shrink-0">
								<div className="w-28">
									<div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
										<div
											className="h-full bg-primary-blue"
											style={{ width: `${(progress.done / progress.total) * 100}%` }}
										/>
									</div>
									<p className="text-[11px] text-gray-500 mt-1">
										{progress.done} of {progress.total} sections started
									</p>
								</div>
								<PermissionChecker permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}>
									<button
										type="button"
										onClick={() => setActiveDraftId(draft.id)}
										className="h-10 px-5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
									>
										Continue
									</button>
									<button
										type="button"
										onClick={() => setDraftToDelete(draft)}
										aria-label="Delete draft"
										title="Delete draft"
										className="h-10 w-10 flex items-center justify-center rounded-lg text-gray-500 hover:text-red-500 hover:bg-red-50"
									>
										<BsTrash />
									</button>
								</PermissionChecker>
							</div>
						</li>
					);
				})}
			</ul>
			<DeleteModal
				openDeleteModal={!!draftToDelete}
				setOpenDeleteModal={(open) => !open && setDraftToDelete(null)}
				handleDeleteMethod={handleDeleteDraft}
				spanContent={
					draftToDelete
						? `the draft "${draftToDelete.tradeName || draftToDelete.companyName || "Untitled draft"}"`
						: undefined
				}
				deleting={deletingDraft}
			/>
		</div>
	);

	if (!request) {
		return (
			<div>
				{header}
				<div className="flex flex-col items-center text-center py-12">
					<div className="h-14 w-14 rounded-full bg-[#26A0F91A] flex items-center justify-center">
						<MdOutlinePayments className="text-2xl text-primary-blue" />
					</div>
					<h3 className="font-semibold text-lg mt-5">
						{drafts.length ? "Finish setting up payments" : "Set up payments for this company"}
					</h3>
					<p className="text-sm text-gray-500 mt-2 max-w-[460px]">
						{drafts.length
							? "You have saved drafts. Continue one below, or start a new setup."
							: "Register the company with the payment provider so it can pay salaries and collect payments. You'll need the company's registration details, director IDs and KYC documents."}
					</p>
					<PermissionChecker permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}>
						<button
							type="button"
							onClick={() => setEditingMode("create")}
							className={`mt-6 h-12 px-8 rounded-lg text-sm ${
								drafts.length
									? "border border-gray-300 text-gray-700 hover:bg-gray-50"
									: "bg-primary-mango text-white"
							}`}
						>
							{drafts.length ? "Start a new setup" : "Start setup"}
						</button>
					</PermissionChecker>
				</div>
				{draftsList}
			</div>
		);
	}

	if (editingMode === "correct" || editingMode === "update") {
		return (
			<div>
				{header}
				<PaymentSetupWizard
					key={`${request.id}-${editingMode}`}
					companyId={companyId}
					mode={editingMode}
					setupId={request.id}
					initialValues={paymentSetupToFormValues(request, countryCode)}
					onCancel={closeEditing}
					onSubmitted={closeEditing}
				/>
			</div>
		);
	}

	const statusDetails = status ? STATUS_DETAILS[status] : undefined;
	const canCorrect =
		status === PaymentSetupStatus.PENDING || status === PaymentSetupStatus.FAILED;
	const isLive = status === PaymentSetupStatus.SUCCESSFUL;

	return (
		<div>
			{header}

			{/* Request header */}
			<div className="flex justify-between items-start gap-6 rounded-lg border border-gray-200 p-5">
				<div className="flex items-start gap-4">
					{request.companyLogo && (
						<img
							src={request.companyLogo}
							alt=""
							className="h-12 w-12 rounded-lg border object-contain shrink-0"
						/>
					)}
					<div>
						<div className="flex items-center gap-3">
							<h3 className="font-semibold text-base">
								{request.tradeName || request.companyName}
							</h3>
							{statusDetails && (
								<span
									className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${statusDetails.className}`}
								>
									{statusDetails.label}
								</span>
							)}
						</div>
						<p className="text-xs text-gray-500 mt-1">
							Submitted {formatDate(request.createdAt)}
							{request.completedAt && ` · Completed ${formatDate(request.completedAt)}`}
							{request.providerRequestId && ` · Reference ${request.providerRequestId}`}
						</p>
						{statusDetails && (
							<p className="text-sm text-gray-600 mt-3 max-w-[560px]">
								{statusDetails.description}
							</p>
						)}
					</div>
				</div>
				<PermissionChecker permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}>
					<div className="flex items-center gap-3 shrink-0">
						{canCorrect && (
							<button
								type="button"
								onClick={() => setEditingMode("correct")}
								className="h-11 px-5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
							>
								Edit request
							</button>
						)}
						{isLive && (
							<>
								<button
									type="button"
									onClick={() => setEditingMode("update")}
									className="h-11 px-5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
								>
									Update details
								</button>
								<button
									type="button"
									onClick={() => setOpenAddProducts(true)}
									className="bg-primary-mango text-white h-11 px-5 rounded-lg text-sm"
								>
									Add products
								</button>
							</>
						)}
						{status === PaymentSetupStatus.REJECTED && (
							<button
								type="button"
								onClick={() => setEditingMode("create")}
								className="bg-primary-mango text-white h-11 px-5 rounded-lg text-sm"
							>
								Start a new request
							</button>
						)}
					</div>
				</PermissionChecker>
			</div>

			{/* Corrections requested by the provider */}
			{issues.length > 0 && (
				<div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 p-5">
					<div className="flex items-center justify-between gap-4">
						<h4 className="flex items-center gap-2 font-semibold text-sm text-amber-800">
							<BsExclamationTriangleFill />
							The provider needs {issues.length} correction{issues.length > 1 ? "s" : ""}
						</h4>
						{canCorrect && (
							<PermissionChecker permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}>
								<button
									type="button"
									onClick={() => setEditingMode("correct")}
									className="text-sm text-primary-blue font-medium"
								>
									Fix and resubmit
								</button>
							</PermissionChecker>
						)}
					</div>
					<ul className="mt-3 space-y-2">
						{issues.map((issue, index) => (
							<li key={index} className="text-sm text-gray-700">
								<span className="font-medium">
									{[humanise(issue.sectionCode), humanise(issue.fieldName)]
										.filter(Boolean)
										.join(" › ")}
									:
								</span>{" "}
								{issue.issue}
							</li>
						))}
					</ul>
				</div>
			)}

			{status === PaymentSetupStatus.REJECTED && draftsList}

			<h4 className="font-semibold text-sm mt-8 mb-3">Submitted details</h4>
			<RequestSummary values={paymentSetupToFormValues(request, countryCode)} />

			{openAddProducts && (
				<AddProductsModal
					open={openAddProducts}
					onClose={() => setOpenAddProducts(false)}
					setupId={request.id}
					tier={request.tier || ""}
					companyName={request.companyName || ""}
				/>
			)}
		</div>
	);
};

export default PaymentSetup;
